import { useCallback, useState } from 'react';
import { Cabecalho, Layout } from '../componentes/Layout.tsx';
import { Botao, Campo } from '../componentes/forma.tsx';
import { Bancada, Busca, Chave, Lista, Painel } from '../componentes/painel.tsx';
import { api, ErroApi } from '../lib/api.ts';
import { useCadastro } from '../lib/cadastro.ts';
import { formatarTelefone, soDigitos } from '../lib/formato.ts';
import { useUsuario } from '../lib/sessao.tsx';

const PERMISSOES = [
  { chave: 'pode_exame_consultar', rotulo: 'Consultar exames', grupo: 'Exames' },
  { chave: 'pode_exame_cadastrar', rotulo: 'Cadastrar exames', grupo: 'Exames' },
  { chave: 'pode_exame_alterar', rotulo: 'Alterar exames', grupo: 'Exames' },
  { chave: 'pode_excluir_imagens', rotulo: 'Excluir imagens', grupo: 'Exames' },
  { chave: 'pode_ocupacional', rotulo: 'Retirar laudos de medicina ocupacional', grupo: 'Exames' },
  { chave: 'pode_avisar_pacientes', rotulo: 'Avisar o paciente pelo WhatsApp', grupo: 'WhatsApp' },
  { chave: 'pode_avisar_medicos', rotulo: 'Chamar os médicos pelo WhatsApp', grupo: 'WhatsApp' },
  { chave: 'pode_pacientes', rotulo: 'Pacientes', grupo: 'Cadastros' },
  { chave: 'pode_empresas', rotulo: 'Empresas', grupo: 'Cadastros' },
  { chave: 'pode_convenios', rotulo: 'Convênios', grupo: 'Cadastros' },
  { chave: 'pode_procedimentos', rotulo: 'Procedimentos e especialidades', grupo: 'Cadastros' },
  { chave: 'pode_medicos', rotulo: 'Médicos', grupo: 'Cadastros' },
  { chave: 'pode_relatorios', rotulo: 'Relatórios', grupo: 'Administração' },
  { chave: 'pode_usuarios', rotulo: 'Usuários', grupo: 'Administração' },
  { chave: 'pode_financeiro', rotulo: 'Valores e faturamento', grupo: 'Administração' },
] as const;

type Chaves = (typeof PERMISSOES)[number]['chave'];

interface Usuario {
  id: string;
  nome: string;
  email: string;
  telefone: string | null;
  ativo: boolean;
  admin: boolean;
  trocarSenha: boolean;
  ultimoAcesso: string | null;
}
type Form = { nome: string; email: string; telefone: string; ativo: boolean; admin: boolean } & Record<
  Chaves,
  boolean
>;

const SEM_PERMISSAO = Object.fromEntries(PERMISSOES.map((p) => [p.chave, false])) as Record<
  Chaves,
  boolean
>;
const VAZIO: Form = { nome: '', email: '', telefone: '', ativo: true, admin: false, ...SEM_PERMISSAO };

const GRUPOS = ['Exames', 'WhatsApp', 'Cadastros', 'Administração'] as const;

export function Usuarios() {
  const eu = useUsuario();
  const [busca, setBusca] = useState('');
  const [recado, setRecado] = useState('');

  const paraFormulario = useCallback((u: Usuario & Record<Chaves, boolean>): Form => {
    const permissoes = Object.fromEntries(
      PERMISSOES.map((p) => [p.chave, Boolean(u[p.chave])]),
    ) as Record<Chaves, boolean>;
    return {
      nome: u.nome,
      email: u.email,
      telefone: u.telefone ?? '',
      ativo: u.ativo,
      admin: u.admin,
      ...permissoes,
    };
  }, []);

  const c = useCadastro<Usuario & Record<Chaves, boolean>, Form>({
    recurso: 'usuarios',
    vazio: VAZIO,
    paraFormulario,
    rotulo: (u) => u.nome,
  });

  const filtrados = busca
    ? c.itens.filter(
        (u) =>
          u.nome.toLowerCase().includes(busca.toLowerCase()) ||
          u.email.toLowerCase().includes(busca.toLowerCase()),
      )
    : c.itens;

  const ehEu = c.editando === eu.id;

  async function redefinirSenha() {
    if (!c.editando) return;
    if (!confirm('Redefinir a senha? Ela volta a ser o e-mail e o usuário troca no próximo acesso.')) return;
    const r = await api.post<{ senha: string }>(`/usuarios/${c.editando}/senha`);
    setRecado(`Senha redefinida para ${r.senha}.`);
  }

  return (
    <Layout>
      <Cabecalho titulo="Usuários" contagem={c.lista.isPending ? '' : `${c.total}`}>
        {c.editando !== null && (
          <Botao onClick={c.limpar} variante="neutro">
            Novo usuário
          </Botao>
        )}
      </Cabecalho>

      <Bancada>
        <Painel
          titulo={c.editando !== null ? 'Editar usuário' : 'Novo usuário'}
          editando={c.editando !== null}
          erro={c.erroGeral}
          salvo={recado || c.salvo}
          salvando={c.salvando}
          rotuloCriar="Cadastrar usuário"
          aoEnviar={c.enviar}
          aoCancelar={() => {
            setRecado('');
            c.limpar();
          }}
          excluindo={c.excluir.isPending}
          aoExcluir={
            ehEu
              ? undefined
              : () => {
                  if (confirm(`Excluir ${c.form.nome}?`)) c.excluir.mutate(c.editando!);
                }
          }
        >
          <Campo
            rotulo="Nome"
            obrigatorio
            value={c.form.nome}
            erro={c.campos.nome}
            onChange={c.mudar('nome')}
          />
          <Campo
            rotulo="E-mail"
            type="email"
            obrigatorio
            largura="meia"
            value={c.form.email}
            erro={c.campos.email}
            onChange={c.mudar('email')}
            dica={c.editando === null ? 'É o login e a senha inicial.' : undefined}
          />
          <Campo
            rotulo="Telefone"
            largura="meia"
            inputMode="numeric"
            value={formatarTelefone(c.form.telefone)}
            erro={c.campos.telefone}
            onChange={c.mudar('telefone', soDigitos)}
          />

          <div className="secao">
            <p className="rotulo secao__titulo">Acesso</p>
          </div>
          <Chave
            rotulo="Ativo"
            descricao={ehEu ? 'Você não pode desativar o próprio acesso.' : 'Acesso desativado impede o login.'}
            checked={c.form.ativo}
            onChange={c.mudar('ativo')}
            disabled={ehEu}
          />
          <Chave
            rotulo="Administrador"
            descricao="Enxerga e altera tudo, sem depender das permissões abaixo."
            checked={c.form.admin}
            onChange={c.mudar('admin')}
            disabled={ehEu}
          />

          {!c.form.admin &&
            GRUPOS.map((grupo) => (
              <div key={grupo} style={{ gridColumn: 'span 12' }}>
                <div className="secao">
                  <p className="rotulo secao__titulo">{grupo}</p>
                </div>
                <div className="grade">
                  {PERMISSOES.filter((p) => p.grupo === grupo).map((p) => (
                    <Chave
                      key={p.chave}
                      rotulo={p.rotulo}
                      checked={c.form[p.chave]}
                      onChange={c.mudar(p.chave)}
                      disabled={ehEu && p.chave === 'pode_usuarios'}
                    />
                  ))}
                </div>
              </div>
            ))}

          {c.editando !== null && (
            <div className="secao">
              <p className="rotulo secao__titulo">Senha</p>
              <Botao type="button" variante="neutro" tamanho="sm" onClick={() => void redefinirSenha()}>
                Redefinir senha
              </Botao>
            </div>
          )}
        </Painel>

        <div>
          <div className="filtros cartao">
            <Busca valor={busca} aoMudar={setBusca} placeholder="Nome ou e-mail" />
          </div>

          <Lista
            carregando={c.lista.isPending}
            recarregando={c.lista.isFetching}
            erro={c.lista.error instanceof ErroApi ? c.lista.error.message : undefined}
            vazio={
              filtrados.length === 0
                ? { titulo: 'Nenhum usuário encontrado', apoio: 'Ajuste a busca.' }
                : undefined
            }
          >
            <table className="tabela">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>E-mail</th>
                  <th>Perfil</th>
                  <th>Situação</th>
                </tr>
              </thead>
              <tbody>
                {filtrados.map((u) => (
                  <tr
                    key={u.id}
                    tabIndex={0}
                    role="button"
                    data-selecionada={c.editando === u.id || undefined}
                    data-inativa={!u.ativo || undefined}
                    onClick={() => c.selecionar(u)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        c.selecionar(u);
                      }
                    }}
                  >
                    <td className="tabela__nome">
                      {u.nome}
                      {u.id === eu.id && <span className="tabela__apoio"> · você</span>}
                    </td>
                    <td>{u.email}</td>
                    <td>{u.admin ? 'Administrador' : 'Operador'}</td>
                    <td>
                      {!u.ativo ? 'Desativado' : u.trocarSenha ? 'Senha pendente' : 'Ativo'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Lista>
        </div>
      </Bancada>
    </Layout>
  );
}
